import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fabric } from 'fabric';
import { BrandColors } from '@hallmark/web.styles.colors';
import { getEditableTextSettings } from '../../../components/card-editor/card-editor-utils';
import {
  getEditableTextButton,
  getTextSettings,
  removeEditableAreaButtons,
} from '../../../components/card-editor/utils';
import { useAnalyticsContext } from '../../../context/analytics-context';
import { useAppContext } from '../../../context/app-context/app-context';
import { setIsTextDrawerOpen } from '../../../context/app-context/app-context-actions';
import { useCardContext } from '../../../context/card-context';
import { useInitializationDataContext } from '../../../context/data-context';
import { CardFaceData, CardText, EditableTextSettings } from '../../../global-types';
import { useFeatureFlags } from '../../../hooks';
import colorsList from '../../../styles/util.scss';
import {
  CanvasDataTypes,
  addUserTextHandlers,
  getMaxTextLines,
  getSizeAfterScaling,
  helperSettingsConfig,
  setEditableTextHandlers,
} from '../../../utils';
import { createEditableInput } from '../../../utils/canvas-settings';
import { getCardFaceClipPath } from '../../../utils/canvas-utils/get-card-face-clip-path';
import { convertPointToPixel } from '../../../utils/fonts';
import { getFontidByUrlName } from '../../../utils/utility/get-fontid-by-url-name';
import { getUserZoneTextboxOffsets } from '../../../utils/utility/get-user-zone-textbox-offsets';
import { generateFabricElementId } from '../utils/generate-fabric-element-id';
import { getDefaultUserTextPosition } from '../utils/get-default-user-text-position';
import { getUserZoneTextPosition } from '../utils/get-user-zone-text-position';

type AddTextAreaProps = {
  canvas: React.RefObject<fabric.Canvas>;
  cardFace: CardFaceData;
  onError?: (error: Error) => void;
};

type AddTemplateTextAreaProps = {
  textAreaSettings: CardText;
  textSettingsIndex: number;
} & AddTextAreaProps;

type LoadTextAreaProps = {
  canvas: React.RefObject<fabric.Canvas>;
  editableText: fabric.Textbox;
};

export const useTextArea = () => {
  const { initializedDataState } = useInitializationDataContext();
  const { t } = useTranslation();
  const { appDispatch } = useAppContext();
  const { cardState } = useCardContext();

  const { updateEditFormats, firstElementSelected } = useAnalyticsContext();
  const { isUK } = initializedDataState;
  const { scalingFactor } = helperSettingsConfig;
  const { IS_TEXTBOX_OVERLAY_ENABLED } = useFeatureFlags();

  const fontSizeAfterScaling = 40;

  // Creates a new custom textbox and adds it to the canvas, such as when the user clicks on the "Add Text" button
  const addCustomTextArea = useCallback(
    ({ canvas, cardFace, onError }: AddTextAreaProps) => {
      // Checks if text can be added to the canvas
      const currentCanvas = canvas?.current as fabric.Canvas;
      if (!currentCanvas) {
        if (onError) {
          const error = new Error(`Error when adding custom text to ${cardFace.type} cardFace: canvas is undefined`);
          onError(error);
        }
        return;
      }

      // Get default text area settings
      const defaultTextAreaSettings = { ...initializedDataState.data?.variables?.template_data?.DefaultTextArea };
      const INITIAL_TEXT_MESSAGE = defaultTextAreaSettings?.Text || (t('cardEditor.userTextDefaultMessage') as string);

      // Colors, font, and settings
      const defaultFallbackColor = isUK ? colorsList[BrandColors.CharcoalGrey] : colorsList[BrandColors.Black];
      const defaultColor = defaultTextAreaSettings?.TextColor || defaultFallbackColor;
      const defaultFontSize = convertPointToPixel(defaultTextAreaSettings?.FontSize || 50);
      defaultTextAreaSettings.FontFamilyId = getFontidByUrlName(
        defaultTextAreaSettings?.FontFamily || '',
        initializedDataState.data?.font_collection?.fonts,
      );

      const defaultTextSettings = getTextSettings({
        cardState,
        color: defaultColor,
        defaultFontSize,
        isUK,
        defaultOptions: defaultTextAreaSettings,
        name: CanvasDataTypes.UserText,
      });

      // TODO: remove this once feature flag is removed
      defaultTextSettings.data.isOverlayTextareaEnabled = true;

      // Position and width
      let zoneDemensions;
      const cardFaceClipPath = getCardFaceClipPath(cardFace, 0);
      // If the card face has a clip path, use it to determine the text area position
      if (cardFaceClipPath) {
        const { TOP_OFFSET, LEFT_OFFSET, RIGHT_OFFSET } = getUserZoneTextboxOffsets(cardState.cardFormat);
        zoneDemensions = getUserZoneTextPosition({
          clipPathTop: cardFaceClipPath.top,
          clipPathLeft: cardFaceClipPath.left,
          clipPathWidth: cardFaceClipPath.width,
          topOffset: TOP_OFFSET,
          leftOffset: LEFT_OFFSET,
          rightOffset: RIGHT_OFFSET,
        });
      } else {
        // If no clip path, use default position logic
        const canvasWidth = currentCanvas.getWidth();
        const canvasZoom = currentCanvas.getZoom();
        const cardFaceType = cardFace.type?.split('-')[0];

        zoneDemensions = getDefaultUserTextPosition({
          cardFormat: cardState.cardFormat,
          cardFaceType,
          canvasWidth,
          canvasZoom,
          scalingFactor,
        });
      }

      // Create textbox
      const textboxID = generateFabricElementId(); // Generate a unique ID for the textbox to be used for overlay placement
      const textElement = new fabric.Textbox(INITIAL_TEXT_MESSAGE, {
        ...defaultTextSettings,
        ...zoneDemensions,
        hasControls: true,
        borderColor: isUK ? '#333333' : '#604099',
        data: { type: CanvasDataTypes.UserText, ID: textboxID, isOverlayTextboxEnabled: IS_TEXTBOX_OVERLAY_ENABLED },
        clipPath: cardFaceClipPath ?? undefined,
      });

      // TODO: remove this once feature flag is removed
      textElement.data.isOverlayTextareaEnabled = true;

      // Adds handles to the text element
      addUserTextHandlers(textElement, INITIAL_TEXT_MESSAGE, defaultColor, currentCanvas);

      removeEditableAreaButtons(currentCanvas);
      currentCanvas.add(textElement);
      currentCanvas.setActiveObject(textElement);

      setIsTextDrawerOpen(appDispatch);
      updateEditFormats({ eventAction: !firstElementSelected?.current ? 'start' : 'add' });
      if (firstElementSelected && !firstElementSelected.current) {
        firstElementSelected.current = true;
      }
    },
    [isUK, scalingFactor, appDispatch, updateEditFormats, firstElementSelected, initializedDataState, cardState, t],
  );

  // Creates new template defined textbox and adds it to the canvas, such as when the card loads
  const addTemplateTextArea = useCallback(
    async ({ canvas, cardFace, textAreaSettings, textSettingsIndex, onError }: AddTemplateTextAreaProps) => {
      const currentCanvas = canvas?.current as fabric.Canvas;
      if (!currentCanvas) {
        if (onError) {
          const error = new Error(`Error when adding text to template ${cardFace.type} cardFace: Canvas is undeifned`);
          onError(error);
        }
      }
      if (textAreaSettings.IsHybrid) {
        return;
      }

      const settings = getEditableTextSettings(textAreaSettings, cardState);

      // TODO: remove this once feature flag is removed
      settings.data.isOverlayTextareaEnabled = true;

      const editableInput = createEditableInput(settings, cardFace.faceId, textSettingsIndex);
      const editIconScale = getSizeAfterScaling(fontSizeAfterScaling, currentCanvas);
      const maxLines = getMaxTextLines(textAreaSettings);
      const image = await getEditableTextButton(settings, editIconScale, isUK);
      await setEditableTextHandlers(image, editableInput, settings.fontSize, maxLines, cardState).then(() => {
        currentCanvas.add(...[editableInput, image]);
      });
    },
    [cardState, isUK, fontSizeAfterScaling],
  );

  // Loads the text area from canvas JSON when card is re-loaded
  const loadTextArea = useCallback(
    async ({ canvas, editableText }: LoadTextAreaProps) => {
      const currentCanvas = canvas?.current as fabric.Canvas;
      if (!currentCanvas) {
        throw new Error('No active canvas found when adding text to canvas');
      }

      const editIconScale = getSizeAfterScaling(fontSizeAfterScaling, currentCanvas);
      const maxLines = editableText.data?.maxLines;
      const maxFontSize = editableText.data?.maxFontSize;
      // using the fabric options as the settings since this was already populated with the settings in the first card load.
      const settings = editableText as unknown as EditableTextSettings;
      getEditableTextButton(settings, editIconScale, isUK).then((icon) => {
        setEditableTextHandlers(icon, editableText, maxFontSize, maxLines, cardState);
        currentCanvas.add(icon);
        currentCanvas.bringToFront(icon);
        currentCanvas.requestRenderAll();
      });
    },
    [cardState, isUK, fontSizeAfterScaling],
  );

  return { addCustomTextArea, addTemplateTextArea, loadTextArea };
};
